import { useEffect } from 'react';
import { replaceAllPins, type PinnedFeed } from './pinnedFeeds';
import { clearWallEscalation, type WallTarget } from './selectionBridge';

/**
 * Wall-mode RESET (#371) — clear operator-placed feeds and return the wall to
 * pure auto-curation.
 *
 * After an operator pins a feed (#182/#216) or escalates / "sends to wall"
 * (#222), there was no way to clear that from the wall display itself — a pin or
 * an escalation would ride the wall until the operator un-pinned it at a desk or
 * the 30-min TTL expired. This is the wall-side "back to default" control.
 *
 * Operator inputs to the wall media zone are exactly two: the pinned-feed set
 * (`pinnedFeeds`) and the active escalation (`selectionBridge`). Clearing BOTH
 * returns `selectWallFeeds` to its alert-driven + ambient auto-curation (the
 * pinned/escalated tiers go empty). Queue working-state (`workstateBridge`,
 * #264 ack/claim/owner) is deliberately NOT touched — that is desk queue state,
 * not a wall feed, and clearing it would silently un-acknowledge live alerts.
 *
 * Both writes reuse the existing persist-and-broadcast paths, so every open
 * window (the COP rail's "On wall" toggle, the operations panel, sibling walls)
 * converges on the cleared state the same beat — there is no wall-only store.
 */

/** True when the wall is carrying operator-placed feeds — at least one pin OR an
 *  active escalation. When false the wall is already in pure auto-curation, so
 *  the reset control hides (nothing to reset) and the auto tiers are untouched. */
export function hasWallOperatorState(
  pins: readonly PinnedFeed[],
  escalation: WallTarget | null,
): boolean {
  return pins.length > 0 || escalation != null;
}

/**
 * Wall-hero RECENTER on reset (#392).
 *
 * "Send to wall" (#222) flies the hero map down onto the escalated target, but
 * nothing flew it back — the tour swaps layers without moving the camera, so the
 * hero stayed zoomed on the (now-cleared) escalation. Reset must therefore also
 * re-assert the locked Canada frame on the hero.
 *
 * The reset control and the hero map live in SIBLING wall subtrees, so they
 * couple through a same-window DOM event — exactly like the COP selection bridge
 * (`COP_SELECT_EVENT`) — rather than a shared ref. Both live in the wall window,
 * so a same-window CustomEvent suffices (no BroadcastChannel needed). The hero
 * subscribes with `useWallRecenter` and flies to `CANADA_VIEW_BOUNDS`.
 */
export const WALL_RECENTER_EVENT = 'tsr:wall-recenter';

/** Fire the recenter request (no-op outside a DOM, e.g. SSR/tests without window). */
export function requestWallRecenter(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(WALL_RECENTER_EVENT));
}

/** Wall-hero side: run `onRecenter` whenever a reset requests the locked frame.
 *  `onRecenter` should be referentially stable (wrap in useCallback) so the
 *  listener isn't re-subscribed every render. */
export function useWallRecenter(onRecenter: () => void): void {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (): void => onRecenter();
    window.addEventListener(WALL_RECENTER_EVENT, handler);
    return () => window.removeEventListener(WALL_RECENTER_EVENT, handler);
  }, [onRecenter]);
}

/** Clear every operator-placed wall feed (all pins + any active escalation),
 *  recenter the hero to the locked Canada frame (#392), and return the wall to
 *  auto-curation. Idempotent: clearing an already-clear wall is a no-op (empty
 *  pin broadcast + escalation removal + a recenter to the frame it already holds). */
export function resetWallOperatorState(): void {
  replaceAllPins([]);
  clearWallEscalation();
  requestWallRecenter();
}
